/**
 * Moon phase: illumination, age, and the times of new moon, quarters and full moon.
 *
 * Two parts, both from Jean Meeus, Astronomical Algorithms (2nd ed., 1998):
 * - chapters 47 and 48 give the Moon's geocentric longitude, latitude and distance (the
 *   full 60-term series in `moon-terms.ts`), from which the elongation from the Sun, the
 *   phase angle and the illuminated fraction follow;
 * - chapter 49 gives the instants of the principal phases as a mean-phase formula plus
 *   25 periodic corrections, accurate to seconds against the US Naval Observatory's tables.
 *
 * Meeus works in Dynamical Time (TD, now TT). Clocks run on UT, so every instant is shifted
 * by ΔT, here the Espenak and Meeus polynomial approximation published with NASA's eclipse
 * canon (https://eclipse.gsfc.nasa.gov/SEhelp/deltatpoly2004.html).
 */

import {
  LUNAR_LATITUDE,
  LUNAR_LONGITUDE_DISTANCE,
  NEW_FULL_TERMS,
  QUARTER_TERMS,
} from "@/lib/tool-kit/astronomy/moon-terms";

const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;
const MS_PER_DAY = 86_400_000;
const UNIX_EPOCH_JD = 2_440_587.5;
const J2000 = 2_451_545;
/** Kilometres in one astronomical unit (IAU 2012: 149 597 870 700 m). */
const KM_PER_AU = 149_597_870.7;
/** Mean synodic month, days (Meeus 49.1). */
export const SYNODIC_MONTH = 29.530_588_861;

function mod360(value: number): number {
  return ((value % 360) + 360) % 360;
}

/**
 * ΔT = TT − UT, in seconds, for a decimal year (Espenak and Meeus, 2006).
 * Before 1800 and after 2150 it falls back on the long-term parabola.
 */
export function deltaT(year: number): number {
  if (year < 1900) {
    return deltaTBefore1900(year);
  }
  if (year < 1920) {
    const t = year - 1900;
    return (
      -2.79 +
      1.494_119 * t -
      0.059_893_9 * t ** 2 +
      0.006_196_6 * t ** 3 -
      0.000_197 * t ** 4
    );
  }
  if (year < 1941) {
    const t = year - 1920;
    return 21.2 + 0.844_93 * t - 0.0761 * t ** 2 + 0.002_093_6 * t ** 3;
  }
  if (year < 1961) {
    const t = year - 1950;
    return 29.07 + 0.407 * t - t ** 2 / 233 + t ** 3 / 2547;
  }
  if (year < 1986) {
    const t = year - 1975;
    return 45.45 + 1.067 * t - t ** 2 / 260 - t ** 3 / 718;
  }
  if (year < 2005) {
    const t = year - 2000;
    return (
      63.86 +
      0.3345 * t -
      0.060_374 * t ** 2 +
      0.001_727_5 * t ** 3 +
      0.000_651_814 * t ** 4 +
      0.000_023_735_99 * t ** 5
    );
  }
  if (year < 2050) {
    const t = year - 2000;
    return 62.92 + 0.322_17 * t + 0.005_589 * t ** 2;
  }
  if (year < 2150) {
    return -20 + 32 * ((year - 1820) / 100) ** 2 - 0.5628 * (2150 - year);
  }
  return -20 + 32 * ((year - 1820) / 100) ** 2;
}

function deltaTBefore1900(year: number): number {
  if (year >= 1860) {
    const t = year - 1860;
    return (
      7.62 +
      0.5737 * t -
      0.251_754 * t ** 2 +
      0.016_806_68 * t ** 3 -
      0.000_447_362_4 * t ** 4 +
      t ** 5 / 233_174
    );
  }
  if (year >= 1800) {
    const t = year - 1800;
    return (
      13.72 -
      0.332_447 * t +
      0.006_861_2 * t ** 2 +
      0.004_111_6 * t ** 3 -
      0.000_374_36 * t ** 4 +
      0.000_012_127_2 * t ** 5 -
      0.000_000_169_9 * t ** 6 +
      0.000_000_000_875 * t ** 7
    );
  }
  return -20 + 32 * ((year - 1820) / 100) ** 2;
}

function decimalYear(jd: number): number {
  return 2000 + (jd - J2000) / 365.25;
}

/** Julian day of a UTC instant. */
export function jdFromMs(ms: number): number {
  return ms / MS_PER_DAY + UNIX_EPOCH_JD;
}

/** UTC instant from a Julian day in Dynamical Time (JDE). */
export function msFromJde(jde: number): number {
  const jdUt = jde - deltaT(decimalYear(jde)) / 86_400;
  return (jdUt - UNIX_EPOCH_JD) * MS_PER_DAY;
}

/** Julian day in Dynamical Time for a UTC instant. */
export function jdeFromMs(ms: number): number {
  const jd = jdFromMs(ms);
  return jd + deltaT(decimalYear(jd)) / 86_400;
}

export interface MoonState {
  /** Geocentric ecliptic longitude, degrees (no nutation). */
  longitude: number;
  /** Geocentric ecliptic latitude, degrees. */
  latitude: number;
  /** Distance from Earth's centre to the Moon's, km. */
  distanceKm: number;
  /** Moon minus Sun in ecliptic longitude, 0–360°: 0 new, 90 first quarter, 180 full. */
  elongation: number;
  /** Angle Sun–Moon–Earth at the Moon, degrees. */
  phaseAngle: number;
  /** Illuminated fraction of the disc, 0–1. */
  illumination: number;
  /** True from new moon to full moon. */
  waxing: boolean;
}

interface LunarArguments {
  lPrime: number;
  d: number;
  m: number;
  mPrime: number;
  f: number;
  e: number;
  a1: number;
  a2: number;
  a3: number;
}

function lunarArguments(t: number): LunarArguments {
  return {
    lPrime: mod360(
      218.316_447_7 +
        481_267.881_234_21 * t -
        0.001_578_6 * t ** 2 +
        t ** 3 / 538_841 -
        t ** 4 / 65_194_000
    ),
    d: mod360(
      297.850_192_1 +
        445_267.111_403_4 * t -
        0.001_881_9 * t ** 2 +
        t ** 3 / 545_868 -
        t ** 4 / 113_065_000
    ),
    m: mod360(
      357.529_109_2 +
        35_999.050_290_9 * t -
        0.000_153_6 * t ** 2 +
        t ** 3 / 24_490_000
    ),
    mPrime: mod360(
      134.963_396_4 +
        477_198.867_505_5 * t +
        0.008_741_4 * t ** 2 +
        t ** 3 / 69_699 -
        t ** 4 / 14_712_000
    ),
    f: mod360(
      93.272_095 +
        483_202.017_523_3 * t -
        0.003_653_9 * t ** 2 -
        t ** 3 / 3_526_000 +
        t ** 4 / 863_310_000
    ),
    e: 1 - 0.002_516 * t - 0.000_007_4 * t ** 2,
    a1: mod360(119.75 + 131.849 * t),
    a2: mod360(53.09 + 479_264.29 * t),
    a3: mod360(313.45 + 481_266.484 * t),
  };
}

function sineSums(a: LunarArguments): { l: number; r: number; b: number } {
  let l = 0;
  let r = 0;
  for (const [d, m, mp, f, coefL, coefR] of LUNAR_LONGITUDE_DISTANCE) {
    const arg = (d * a.d + m * a.m + mp * a.mPrime + f * a.f) * RAD;
    const factor = a.e ** Math.abs(m);
    l += coefL * factor * Math.sin(arg);
    r += coefR * factor * Math.cos(arg);
  }
  let b = 0;
  for (const [d, m, mp, f, coefB] of LUNAR_LATITUDE) {
    const arg = (d * a.d + m * a.m + mp * a.mPrime + f * a.f) * RAD;
    b += coefB * a.e ** Math.abs(m) * Math.sin(arg);
  }
  l +=
    3958 * Math.sin(a.a1 * RAD) +
    1962 * Math.sin((a.lPrime - a.f) * RAD) +
    318 * Math.sin(a.a2 * RAD);
  b +=
    -2235 * Math.sin(a.lPrime * RAD) +
    382 * Math.sin(a.a3 * RAD) +
    175 * Math.sin((a.a1 - a.f) * RAD) +
    175 * Math.sin((a.a1 + a.f) * RAD) +
    127 * Math.sin((a.lPrime - a.mPrime) * RAD) -
    115 * Math.sin((a.lPrime + a.mPrime) * RAD);
  return { l, r, b };
}

/** The Sun's true geocentric longitude (degrees) and distance (AU), low-accuracy series. */
function sunLongitudeAndDistance(t: number): { longitude: number; au: number } {
  const l0 = mod360(280.466_46 + 36_000.769_83 * t + 0.000_303_2 * t ** 2);
  const m = mod360(357.529_11 + 35_999.050_29 * t - 0.000_153_7 * t ** 2) * RAD;
  const centre =
    (1.914_602 - 0.004_817 * t - 0.000_014 * t ** 2) * Math.sin(m) +
    (0.019_993 - 0.000_101 * t) * Math.sin(2 * m) +
    0.000_289 * Math.sin(3 * m);
  const ecc = 0.016_708_634 - 0.000_042_037 * t - 0.000_000_126_7 * t ** 2;
  const trueAnomaly = m + centre * RAD;
  const au =
    (1.000_001_018 * (1 - ecc * ecc)) / (1 + ecc * Math.cos(trueAnomaly));
  return { longitude: mod360(l0 + centre), au };
}

/** The Moon's state at a Julian day in Dynamical Time. */
export function moonStateAtJde(jde: number): MoonState {
  const t = (jde - J2000) / 36_525;
  const args = lunarArguments(t);
  const sums = sineSums(args);
  const longitude = mod360(args.lPrime + sums.l / 1_000_000);
  const latitude = sums.b / 1_000_000;
  const distanceKm = 385_000.56 + sums.r / 1000;
  const sun = sunLongitudeAndDistance(t);
  // Aberration of the Sun's light, so the elongation compares apparent places; nutation
  // moves both longitudes equally and cancels.
  const sunApparent = sun.longitude - 0.005_69;
  const elongation = mod360(longitude - sunApparent);
  const cosPsi =
    Math.cos(latitude * RAD) * Math.cos((longitude - sunApparent) * RAD);
  const psi = Math.acos(Math.min(1, Math.max(-1, cosPsi)));
  const sunKm = sun.au * KM_PER_AU;
  const phaseAngle =
    Math.atan2(sunKm * Math.sin(psi), distanceKm - sunKm * Math.cos(psi)) * DEG;
  return {
    longitude,
    latitude,
    distanceKm,
    elongation,
    phaseAngle,
    illumination: (1 + Math.cos(phaseAngle * RAD)) / 2,
    waxing: elongation < 180,
  };
}

/** The Moon's state at a UTC instant. */
export function moonState(ms: number): MoonState {
  return moonStateAtJde(jdeFromMs(ms));
}

export type PhaseKind = "new" | "first-quarter" | "full" | "last-quarter";

const PHASE_ORDER: readonly PhaseKind[] = [
  "new",
  "first-quarter",
  "full",
  "last-quarter",
];

/**
 * The Julian day (Dynamical Time) of a principal phase. `k` is an integer for a new moon,
 * plus 0.25 for first quarter, 0.5 for full moon, 0.75 for last quarter; k = 0 is the new
 * moon of 6 January 2000.
 */
export function phaseJde(k: number): number {
  const t = k / 1236.85;
  const base =
    2_451_550.097_66 +
    29.530_588_861 * k +
    0.000_154_37 * t ** 2 -
    0.000_000_15 * t ** 3 +
    0.000_000_000_73 * t ** 4;
  const e = 1 - 0.002_516 * t - 0.000_007_4 * t ** 2;
  const m =
    mod360(
      2.5534 + 29.105_356_7 * k - 0.000_001_4 * t ** 2 - 0.000_000_11 * t ** 3
    ) * RAD;
  const mPrime =
    mod360(
      201.5643 +
        385.816_935_28 * k +
        0.010_758_2 * t ** 2 +
        0.000_012_38 * t ** 3 -
        0.000_000_058 * t ** 4
    ) * RAD;
  const f =
    mod360(
      160.7108 +
        390.670_502_84 * k -
        0.001_611_8 * t ** 2 -
        0.000_002_27 * t ** 3 +
        0.000_000_011 * t ** 4
    ) * RAD;
  const omega =
    mod360(
      124.7746 - 1.563_755_88 * k + 0.002_067_2 * t ** 2 + 0.000_002_15 * t ** 3
    ) * RAD;
  const quarter = Math.round((k - Math.floor(k)) * 4) % 4;
  let correction = 0;
  const source =
    quarter === 0 || quarter === 2 ? NEW_FULL_TERMS : QUARTER_TERMS;
  const column = quarter === 2 ? 5 : 4;
  for (const row of source) {
    const arg = row[0] * m + row[1] * mPrime + row[2] * f + row[3] * omega;
    correction += row[column] * e ** Math.abs(row[0]) * Math.sin(arg);
  }
  if (quarter === 1 || quarter === 3) {
    const w =
      0.003_06 -
      0.000_38 * e * Math.cos(m) +
      0.000_26 * Math.cos(mPrime) -
      0.000_02 * Math.cos(mPrime - m) +
      0.000_02 * Math.cos(mPrime + m) +
      0.000_02 * Math.cos(2 * f);
    correction += quarter === 1 ? w : -w;
  }
  return base + correction + planetaryTerms(k, t);
}

function planetaryTerms(k: number, t: number): number {
  const a = [
    299.77 + 0.107_408 * k - 0.009_173 * t ** 2,
    251.88 + 0.016_321 * k,
    251.83 + 26.651_886 * k,
    349.42 + 36.412_478 * k,
    84.66 + 18.206_239 * k,
    141.74 + 53.303_771 * k,
    207.14 + 2.453_732 * k,
    154.84 + 7.306_86 * k,
    34.52 + 27.261_239 * k,
    207.19 + 0.121_824 * k,
    291.34 + 1.844_379 * k,
    161.72 + 24.198_154 * k,
    239.56 + 25.513_099 * k,
    331.55 + 3.592_518 * k,
  ];
  const coefficients = [
    0.000_325, 0.000_165, 0.000_164, 0.000_126, 0.000_11, 0.000_062, 0.000_06,
    0.000_056, 0.000_047, 0.000_042, 0.000_04, 0.000_037, 0.000_035, 0.000_023,
  ];
  let sum = 0;
  for (let i = 0; i < a.length; i += 1) {
    sum += coefficients[i] * Math.sin(mod360(a[i]) * RAD);
  }
  return sum;
}

export interface MoonPhaseEvent {
  kind: PhaseKind;
  /** UTC instant, milliseconds since the epoch. */
  time: number;
}

/** The k of the first new moon at or before a UTC instant, roughly. */
function kNear(ms: number): number {
  return Math.floor((decimalYear(jdFromMs(ms)) - 2000) * 12.3685);
}

/** Every principal phase from `startMs` up to (not including) `endMs`, in time order. */
export function phasesBetween(
  startMs: number,
  endMs: number
): MoonPhaseEvent[] {
  const events: MoonPhaseEvent[] = [];
  for (let k = kNear(startMs) - 2; k <= kNear(endMs) + 2; k += 0.25) {
    const kind = PHASE_ORDER[Math.round((k - Math.floor(k)) * 4) % 4];
    const time = msFromJde(phaseJde(k));
    if (time >= startMs && time < endMs) {
      events.push({ kind, time });
    }
  }
  return events.sort((a, b) => a.time - b.time);
}

const DAYS_MS = MS_PER_DAY;

/** The next `count` principal phases after an instant. */
export function nextPhases(ms: number, count: number): MoonPhaseEvent[] {
  return phasesBetween(ms + 1, ms + 45 * DAYS_MS).slice(0, count);
}

/** The most recent new moon at or before an instant, and the next one after it. */
export function surroundingNewMoons(ms: number): {
  previous: number;
  next: number;
} {
  const events = phasesBetween(ms - 40 * DAYS_MS, ms + 40 * DAYS_MS).filter(
    (event) => event.kind === "new"
  );
  let previous = events[0]?.time ?? ms;
  let next = events.at(-1)?.time ?? ms;
  for (const event of events) {
    if (event.time <= ms) {
      previous = event.time;
    } else {
      next = event.time;
      break;
    }
  }
  return { previous, next };
}

/** The Moon's age in days: time since the last new moon. */
export function moonAgeDays(ms: number): number {
  return (ms - surroundingNewMoons(ms).previous) / DAYS_MS;
}

export type PhaseName =
  | "new"
  | "waxing-crescent"
  | "first-quarter"
  | "waxing-gibbous"
  | "full"
  | "waning-gibbous"
  | "last-quarter"
  | "waning-crescent";

/** Half a day of the Moon's mean elongation rate (12.19° a day): the window for a named phase. */
const NAMED_WINDOW = 6.1;

/** The phase name for an elongation: the principal four within ±6.1° (about 12 h), the rest between. */
export function phaseName(elongation: number): PhaseName {
  const e = mod360(elongation);
  const near = (angle: number): boolean => {
    const diff = Math.abs(e - angle);
    return Math.min(diff, 360 - diff) < NAMED_WINDOW;
  };
  if (near(0)) {
    return "new";
  }
  if (near(90)) {
    return "first-quarter";
  }
  if (near(180)) {
    return "full";
  }
  if (near(270)) {
    return "last-quarter";
  }
  if (e < 90) {
    return "waxing-crescent";
  }
  if (e < 180) {
    return "waxing-gibbous";
  }
  if (e < 270) {
    return "waning-gibbous";
  }
  return "waning-crescent";
}
